import React, { ReactNode } from "react";
import { ActiveElement, ChartEvent, ChartOptions } from "chart.js";
import { get, map } from "lodash";
import { shorten } from "@backpacker/number";
import Tooltip from "@charty/tooltip/chartjs-simple-serie";
import { Color } from "@glossy";
import { chart as chartColor, content } from "@glossy/theme";
import { GlossyTheme } from "@glossy/theme/types";
import { Interval } from "@linkfluence/models/date";
import { AbstractDate } from "@linkfluence/models/date";
import { getOnChangeCanvasCursorEvent } from "@linkfluence/models/events/onChangeCanvasCursor";
import { Locale } from "@linkfluence/models/locale";
import { Timezone } from "@linkfluence/models/timezone";
import { isFloat } from "@tootools/number";
import { Serie } from "../models";
import {
  closeCustomTooltip,
  openCustomTooltip,
} from "../tooltip/chartjs-tooltip-root";
import { formatXAxisBottomLabel, formatXAxisTopLabel } from "../utils";

type TooltipSerie = {
  count: number;
  label: string;
  color: string;
  date: AbstractDate;
};

export const getOptions = (
  theme: GlossyTheme,
  timeUnit: Interval,
  locale: Locale,
  timezone: Timezone,
  onClick: (datasetIndex: number, index: number) => void,
  series: Serie[],
  chartTooltip?: (series: TooltipSerie[]) => ReactNode,
  config?: Partial<ChartOptions>,
  scale?: "logarithmic" | "linear",
  setHasAnimated: (hasAnimated: boolean) => void = () => null,
  hasAnimated = false,
  isFullscreen: boolean = false
): ChartOptions<"line"> => ({
  animation: {
    onComplete: () => {
      if (!hasAnimated) {
        return setHasAnimated(true);
      }
    },
    duration: hasAnimated ? 0 : 1000,
  },
  onClick: function (evt) {
    if (onClick) {
      if (!isFullscreen) {
        closeCustomTooltip();
      }
      const elements = this.getElementsAtEventForMode(
        evt,
        "nearest",
        { intersect: true },
        false
      );
      if (elements && elements[0]) {
        onClick(elements[0].datasetIndex, elements[0].index);
      }
    }
  },
  scales: {
    x: {
      ticks: {
        color: content({ theme }, "secondary"),
        callback: formatXAxisBottomLabel(timeUnit, locale, timezone, series),
      },
      border: {
        display: false,
      },
      grid: {
        display: false,
      },
    },
    x2: {
      position: "top",
      ticks: {
        autoSkip: false,
        maxRotation: 0,
        color: content({ theme }, "secondary"),
        callback: formatXAxisTopLabel(timeUnit, locale, timezone, series),
      },
      border: {
        display: false,
      },
      grid: {
        display: true,
        color: content({ theme }, "tertiary"),
        lineWidth: 0.5,
      },
    },
    y: {
      type: scale,
      beginAtZero: true,
      ticks: {
        labelOffset: 0,
        color: content({ theme }, "secondary"),
        callback: (value: any) =>
          isFloat(value) ? undefined : shorten(locale, value),
      },
      border: {
        display: false,
      },
      grid: {
        tickLength: 30,
        display: true,
        color: content({ theme }, "tertiary"),
        lineWidth: 0.5,
      },
    },
  },
  hover: {
    mode: "index",
    intersect: false,
    ...get(config, "hover"),
  },
  onHover: (event: ChartEvent, elements: ActiveElement[]) => {
    if (onClick && event.native.target instanceof HTMLElement) {
      const cursor = elements.length ? "var(--cursor-magic-wand)" : "default";
      if (event.native.target.style.cursor !== cursor) {
        event.native.target.style.cursor = cursor;
        event.native.target.dispatchEvent(getOnChangeCanvasCursorEvent());
      }
    }
  },
  maintainAspectRatio: false,
  plugins: {
    //@ts-ignore: datalabels is properly imported and registered on the chart
    datalabels: {
      display: false,
    },
    legend: {
      display: false,
    },
    tooltip: {
      mode: "index",
      intersect: false,
      caretPadding: 24,
      enabled: isFullscreen,
      xPadding: 24,
      yPadding: 24,
      backgroundColor: Color.Black,
      titleFontColor: Color.White,
      titleFontSize: 14,
      bodyFontColor: Color.White,
      bodyFontSize: 14,
      bodySpacing: 12,
      callbacks: {
        title: (tooltipModel) => {
          return new Date(tooltipModel[0].label).toLocaleString(
            locale.split("_")[0],
            {
              year: "numeric",
              month: "long",
              day: "numeric",
            }
          );
        },
        label: (tooltipModel) => {
          const dataIdx = tooltipModel["dataIndex"];
          const idx = tooltipModel["datasetIndex"];
          const data = tooltipModel.chart.data;
          const value = data["datasets"][idx].data[dataIdx] as number;
          const label = data["datasets"][idx].label;
          return ` ${label as string}: ${shorten(locale, value)}`;
        },
      },
      external: (context) => {
        if (isFullscreen) {
          return null;
        }

        const { chart, tooltip } = context;

        if (!tooltip.opacity) {
          closeCustomTooltip();
          return;
        }

        const dataPoints = tooltip.dataPoints;
        const dataSeries = map(dataPoints, (dataPoint) => {
          const datasetIndex = dataPoint.datasetIndex;
          const index = dataPoint.dataIndex;
          return {
            label: series[datasetIndex].label,
            color:
              series[datasetIndex].color || chartColor({ theme }, "primary"),
            date: series[datasetIndex].data[index].date,
            count: series[datasetIndex].data[index].count,
          };
        });

        openCustomTooltip(
          tooltip,
          chart,
          chartTooltip ? (
            chartTooltip(dataSeries)
          ) : (
            <Tooltip series={dataSeries} locale={locale} />
          )
        );
      },

      ...get(config, "plugins.tooltip"),
    },
  },
});
